import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";
import AdminBriefingDispatcher from "@/components/adminone/AdminBriefingDispatcher";
import AdminProviderStatusPanel from "@/components/adminone/AdminProviderStatusPanel";
import AdminAnimationSettingsPanel from "@/components/adminone/AdminAnimationSettingsPanel";
import AdminPersonalizationCommand from "@/components/adminone/AdminPersonalizationCommand";
import AdminSwarmCommandPanel from "@/components/adminone/AdminSwarmCommandPanel";

/**
 * CareLogix™ ORACLE — AdminOne Daily CEO Briefing Center (Phase 26).
 *
 * Unified surface that displays:
 *   • 8AM dashboard briefing
 *   • 9AM email schedule (CEO inbox)
 *   • Multi-device notification framework (9 channels)
 *   • 12-point briefing rubric
 *   • Top opportunities + Highest confidence + Startup/Grant signals
 */

const CHANNEL_BADGE = {
  READY:               { color: "#10b981", label: "READY" },
  PENDING_CONNECTOR:   { color: "#c5a059", label: "PENDING CONNECTOR" },
  PLACEHOLDER:         { color: "#94a3b8", label: "PLACEHOLDER" },
};

export default function AdminBriefingCenter() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data: payload } = await adminApi.briefingCenterFull();
      setData(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={S.canvas} data-testid="briefing-center-page">
      {/* Phase 26C — Mandated Compliance Lock™ banner */}
      <ComplianceLock variant="banner" testid="briefing-compliance-top" />
      <div style={S.banner}>
        <span style={{ color: "#10b981" }}>●</span>
        <span>
          <strong>Daily CEO Briefing Center</strong> · 8AM dashboard · 9AM email to{" "}
          <code style={S.codeInline}>contact@carelogix360.com</code> · multi-device notifications ·
          research only · not financial advice
        </span>
      </div>

      <header style={S.header}>
        <div>
          <h1 style={S.title}>CEO Briefing Center™</h1>
          <p style={S.subtitle}>
            CEO-grade daily intelligence brief composed by LOGIX-2™ + NiSi™ across all
            asset classes and signal domains. Delivered to multiple devices on the morning
            schedule. Human review is required before any action.
          </p>
        </div>
        <div style={S.headerActions}>
          <span style={S.livePill} data-testid="briefing-live-pill">SCHEDULE LIVE</span>
          <button type="button" onClick={load} disabled={busy} style={S.refreshBtn} data-testid="briefing-refresh">
            {busy ? "…" : "↻ Refresh Briefing"}
          </button>
        </div>
      </header>

      {err && <div style={S.errBanner} data-testid="briefing-error">⚠ {err}</div>}
      {!data && !err && <p style={S.empty}>Composing LOGIX-2™ daily brief…</p>}

      {data && (
        <main style={S.main}>
          {/* Phase 28D — Dispatcher panel (SMS / Email / PDF / history) */}
          <AdminBriefingDispatcher testid="briefing-dispatcher" />

          {/* Phase 29E — Unified provider status (additive; below dispatcher) */}
          <AdminProviderStatusPanel testid="provider-status-panel" />

          {/* Phase 30H — Animation settings (additive; org-wide operator motion controls) */}
          <AdminAnimationSettingsPanel testid="admin-animation-settings-panel" />

          {/* Phase 31 — Personalization Command (additive; user-intel overview + controls) */}
          <AdminPersonalizationCommand testid="admin-personalization-command" />

          {/* Phase 32A — Agent Swarm Approval Queue (additive; HITL governance for high-risk traces) */}
          <AdminSwarmCommandPanel testid="admin-swarm-command-panel" />

          {/* Schedule + Notification channels */}
          <section style={S.panel} data-testid="briefing-schedule-section">
            <h2 style={S.heading}>
              Delivery Schedule &amp; Multi-Device Notification Framework
              <span style={S.countChip}>{(data.notification_channels || []).length} channels</span>
            </h2>
            <div style={S.scheduleGrid}>
              <div style={S.scheduleCell}>
                <span style={S.scheduleLabel}>DASHBOARD</span>
                <strong style={S.scheduleVal}>{data.delivery_schedule?.dashboard_8am_utc || "08:00"} UTC</strong>
                <span style={S.scheduleHint}>AdminOne 8AM briefing dashboard</span>
              </div>
              <div style={S.scheduleCell}>
                <span style={S.scheduleLabel}>EMAIL</span>
                <strong style={S.scheduleVal}>{data.delivery_schedule?.email_9am_utc || "09:00"} UTC</strong>
                <span style={S.scheduleHint}>contact@carelogix360.com</span>
              </div>
              <div style={S.scheduleCell}>
                <span style={S.scheduleLabel}>SCHEDULER</span>
                <strong style={S.scheduleVal}>Phase 17</strong>
                <span style={S.scheduleHint}>oracle_daily_brief_scheduler</span>
              </div>
            </div>

            <div style={S.channelGrid}>
              {(data.notification_channels || []).map((ch) => {
                const badge = CHANNEL_BADGE[ch.status] || CHANNEL_BADGE.PLACEHOLDER;
                return (
                  <div key={ch.channel} style={S.channelCard} data-testid={`briefing-channel-${ch.channel}`}>
                    <div style={S.channelHead}>
                      <strong style={S.channelLabel}>{ch.label}</strong>
                      <span style={{ ...S.statusPill, color: badge.color, borderColor: badge.color, background: `${badge.color}1a` }}>
                        {badge.label}
                      </span>
                    </div>
                    <div style={S.metaRow}><span style={S.metaLabel}>TRANSPORT</span><code style={S.metaVal}>{ch.transport}</code></div>
                    <div style={S.metaRow}><span style={S.metaLabel}>REQUIRES</span><code style={S.metaVal}>{ch.requires}</code></div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Briefing rubric */}
          <section style={S.panel} data-testid="briefing-rubric-section">
            <h2 style={S.heading}>
              12-Point CEO Briefing Rubric
              <span style={S.countChip}>{(data.briefing_rubric || []).length} fields</span>
            </h2>
            <ul style={S.rubricGrid}>
              {(data.briefing_rubric || []).map((r, i) => (
                <li key={`rb-${i}`} style={S.rubricItem} data-testid={`briefing-rubric-${i}`}>
                  <span style={S.rubricBullet}>{i + 1}</span>
                  <span style={S.rubricText}>{r}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Compliance locks */}
          <section style={S.panel} data-testid="briefing-compliance-section">
            <h2 style={S.heading}>Compliance Locks</h2>
            <div style={S.complianceGrid}>
              {(data.compliance_locks || []).map((lock, i) => (
                <div key={`cl-${i}`} style={S.complianceCard}>⚠ {lock}</div>
              ))}
            </div>
          </section>

          {/* Top opportunities snapshot */}
          {data.top_opportunities?.length > 0 && (
            <section style={S.panel} data-testid="briefing-top-section">
              <h2 style={S.heading}>
                Top Cross-Asset Opportunities
                <span style={S.countChip}>{data.top_opportunities.length} signals</span>
              </h2>
              <div style={S.signalGrid}>
                {data.top_opportunities.map((s, i) => (
                  <div key={`top-${i}`} style={S.signalCard} data-testid={`briefing-top-${i}`}>
                    <div style={S.cardHead}>
                      <strong style={S.cardTitle}>{s.symbol}</strong>
                      <span style={{ ...S.statusPill, color: s.direction === "bullish" ? "#10b981" : (s.direction === "bearish" ? "#ef4444" : "#c5a059"), borderColor: "currentColor" }}>
                        {(s.direction || "").toUpperCase()}
                      </span>
                    </div>
                    <div style={S.metaRow}><span style={S.metaLabel}>ASSET</span><code style={S.metaVal}>{s.asset_class}</code></div>
                    <div style={S.metaRow}><span style={S.metaLabel}>OPP · CONF · RISK</span>
                      <code style={{ ...S.metaVal, color: "#c5a059" }}>
                        {s.opportunity_score}% / {s.confidence_score}% / {s.risk_score}%
                      </code>
                    </div>
                    <ComplianceLock variant="inline" short testid={`briefing-top-${i}-compliance`} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Startup + Grant signals */}
          {(data.startup_signals?.length > 0 || data.grant_signals?.length > 0) && (
            <section style={S.panel} data-testid="briefing-sg-section">
              <h2 style={S.heading}>Startup &amp; Grant Signals</h2>
              <div style={S.signalGrid}>
                {(data.startup_signals || []).map((s, i) => (
                  <div key={`sg-s-${i}`} style={S.signalCard} data-testid={`briefing-startup-${i}`}>
                    <strong style={S.cardTitle}>🚀 {s.name}</strong>
                    <div style={S.metaRow}><span style={S.metaLabel}>SECTOR</span><code style={S.metaVal}>{s.sector}</code></div>
                    <div style={S.metaRow}><span style={S.metaLabel}>RAISE</span><code style={{ ...S.metaVal, color: "#c5a059" }}>${s.raise_usd_m}M</code></div>
                    <div style={S.metaRow}><span style={S.metaLabel}>OPP</span><code style={S.metaVal}>{s.opportunity_score}%</code></div>
                  </div>
                ))}
                {(data.grant_signals || []).map((g, i) => (
                  <div key={`sg-g-${i}`} style={S.signalCard} data-testid={`briefing-grant-${i}`}>
                    <strong style={S.cardTitle}>💼 {g.name}</strong>
                    <div style={S.metaRow}><span style={S.metaLabel}>AGENCY</span><code style={S.metaVal}>{g.agency}</code></div>
                    <div style={S.metaRow}><span style={S.metaLabel}>DEADLINE</span><code style={S.metaVal}>{g.deadline_days}d</code></div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.disclaimer && (
            <footer style={S.discl} data-testid="briefing-disclaimer">{data.disclaimer}</footer>
          )}
        </main>
      )}
    </div>
  );
}

const S = {
  canvas: { minHeight: "100vh", background: "radial-gradient(circle at top left, rgba(16,185,129,0.10), #040608 30%, #020304 100%)", color: "#f8fafc", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  banner: { display: "flex", alignItems: "center", gap: 10, padding: "9px 22px", background: "linear-gradient(90deg, rgba(16,185,129,0.10), rgba(197,160,89,0.06))", borderBottom: "1px solid rgba(16,185,129,0.18)", fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.04em", color: "#d1fae5" },
  codeInline: { color: "#c5a059", background: "rgba(197,160,89,0.10)", padding: "1px 6px", borderRadius: 6 },
  header: { padding: "20px 26px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  title: { color: "#c5a059", fontSize: 24, margin: 0, fontWeight: 800 },
  subtitle: { color: "#94a3b8", fontSize: 13, maxWidth: 820, marginTop: 6 },
  headerActions: { display: "flex", gap: 8, alignItems: "center" },
  livePill: { color: "#10b981", border: "1px solid rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.10)", padding: "6px 11px", borderRadius: 999, fontSize: 10.5, fontWeight: 900, fontFamily: "monospace", letterSpacing: "0.10em" },
  refreshBtn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em" },
  errBanner: { margin: "10px 26px 0", padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12.5 },
  main: { padding: "20px 26px 30px", display: "flex", flexDirection: "column", gap: 16 },
  panel: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.14)", borderRadius: 16, padding: 22 },
  heading: { color: "#e2e8f0", marginTop: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 16, flexWrap: "wrap" },
  countChip: { fontSize: 11, fontFamily: "'Space Mono', monospace", letterSpacing: "0.08em", background: "rgba(197,160,89,0.10)", color: "#c5a059", border: "1px solid rgba(197,160,89,0.28)", padding: "3px 9px", borderRadius: 999 },
  scheduleGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, marginTop: 12 },
  scheduleCell: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 4 },
  scheduleLabel: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em" },
  scheduleVal: { color: "#c5a059", fontSize: 18 },
  scheduleHint: { color: "#94a3b8", fontSize: 11, marginTop: 2 },
  channelGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginTop: 14 },
  channelCard: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 5 },
  channelHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  channelLabel: { color: "#e2e8f0", fontSize: 13 },
  statusPill: { fontSize: 9.5, fontFamily: "'Space Mono', monospace", letterSpacing: "0.10em", fontWeight: 800, padding: "3px 8px", border: "1px solid", borderRadius: 999 },
  metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 3 },
  metaLabel: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.12em" },
  metaVal: { background: "rgba(255,255,255,0.04)", color: "#cbd5e1", padding: "2px 6px", borderRadius: 6, fontFamily: "'Space Mono', monospace", fontSize: 10.5 },
  rubricGrid: { listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 },
  rubricItem: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 },
  rubricBullet: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", background: "rgba(197,160,89,0.18)", color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  rubricText: { color: "#cbd5e1", fontSize: 12.5 },
  complianceGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8, marginTop: 12 },
  complianceCard: { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#fecaca", padding: 12, borderRadius: 10, fontSize: 12.5, fontWeight: 600 },
  signalGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10, marginTop: 12 },
  signalCard: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 5 },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  cardTitle: { color: "#e2e8f0", fontSize: 13 },
  empty: { color: "#94a3b8", textAlign: "center", padding: 28, fontFamily: "'Space Mono', monospace", fontSize: 12.5 },
  discl: { padding: "16px 0 0", color: "#64748b", fontSize: 11.5, fontStyle: "italic", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.04)", marginTop: 8 },
};
